import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { USERS_API_URL } from '../../config/apiConfig';
import { WikiBadge, TrustProgressBar, DiscussionBadge, DiscussionProgressBar, IndustryVIPBadge } from '../ContributorsScreen/ContributorsScreen';
import UpgradeModal from '../UpgradeModal/UpgradeModal';
import UploadOverlay from '../UploadOverlay/UploadOverlay';
import './UserProfileScreen.css';

const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const UserProfileScreen = () => {
  const { user, token, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  
  // Billing state
  const [billing, setBilling] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [billingBanner, setBillingBanner] = useState(null);

  // Wiki stats
  const [wikiStats, setWikiStats] = useState(null);

  // Discussion stats
  const [discStats, setDiscStats] = useState(null);

  // Delete account
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Notification preferences (email digest frequency)
  const [notifPref, setNotifPref] = useState('none');
  const [notifPrefSaving, setNotifPrefSaving] = useState(false);
  const [notifPrefSuccess, setNotifPrefSuccess] = useState('');
  const [notifPrefError, setNotifPrefError] = useState('');

  // Watched items
  const [watchedItems, setWatchedItems] = useState(null); // null = not loaded
  const [watchedLoading, setWatchedLoading] = useState(false);
  const [unwatching, setUnwatching] = useState({}); // { subscription_id: true }
  
  const [formData, setFormData] = useState({
    username: '',
    nickname: '',
    email: '',
    alternate_email: '',
    phone: ''
  });

  useEffect(() => {
    if (user) {
      fetchUserData();
      fetchUserRoles();
      fetchBilling();
      fetchWikiStats();
      fetchDiscStats();
      fetchNotificationPreference();
      fetchWatchedItems();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for email change success or billing result
  useEffect(() => {
    if (searchParams.get('emailChanged') === 'true') {
      setSuccess('Your email address has been updated successfully!');
      navigate('/profile', { replace: true });
      if (user) {
        fetchUserData();
        refreshUser();
      }
    }
    const billingParam = searchParams.get('billing');
    if (billingParam === 'success') {
      setBillingBanner({ type: 'success', message: '🎉 Welcome to AmpNebula Pro! Your subscription is now active.' });
      navigate('/profile', { replace: true });
      // Refresh billing data after a short delay to let webhook complete
      setTimeout(() => fetchBilling(), 1500);
    } else if (billingParam === 'canceled') {
      setBillingBanner({ type: 'info', message: 'Checkout was canceled. No charges were made.' });
      navigate('/profile', { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserData = async () => {
    try {
      const response = await fetch(`${USERS_API_URL}/users/${user.user_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setFormData({
          username: userData.username || '',
          nickname: userData.nickname || '',
          email: userData.email || '',
          alternate_email: userData.alternate_email || '',
          phone: userData.phone || ''
        });
        setEmailStatus({
          status: userData.email_status || 'active',
          reason: userData.email_status_reason,
          updatedAt: userData.email_status_updated_at
        });
        
        if (userData.has_photo) {
          setPhotoPreview(`${USERS_API_URL}/users/${user.user_id}/photo`);
        }
      } else {
        setError('Failed to load user data');
      }
    } catch (err) {
      setError('Error loading user data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserRoles = async () => {
    try {
      const response = await fetch(`${USERS_API_URL}/users/${user.user_id}/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserRoles(data);
      }
    } catch (err) {
      console.error('Error fetching user roles:', err);
    }
  };

  const fetchBilling = useCallback(async () => {
    try {
      const [statusResp, historyResp] = await Promise.all([
        fetch(`${API_URL}/billing/status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_URL}/billing/history`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);
      if (statusResp.ok) setBilling(await statusResp.json());
      if (historyResp.ok) setBillingHistory(await historyResp.json());
    } catch (err) {
      console.error('Failed to load billing:', err);
    }
  }, [token]);

  const fetchWikiStats = async () => {
    try {
      const res = await fetch(`${API_URL}/wiki/stats/${user.user_id || user.userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setWikiStats(await res.json());
    } catch (err) {
      console.error('Failed to load wiki stats:', err);
    }
  };

  const fetchDiscStats = async () => {
    try {
      const res = await fetch(`${API_URL}/discussions/stats/${user.user_id || user.userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setDiscStats(await res.json());
    } catch (err) {
      console.error('Failed to load discussion stats:', err);
    }
  };

  const fetchNotificationPreference = async () => {
    try {
      const res = await fetch(`${API_URL}/users/me/notification-preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifPref(data.email_digest_frequency || 'none');
      }
    } catch (err) {
      console.error('Failed to load notification preference:', err);
    }
  };

  const fetchWatchedItems = async () => {
    setWatchedLoading(true);
    try {
      const res = await fetch(`${API_URL}/subscriptions/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWatchedItems(data.subscriptions || []);
      }
    } catch (err) {
      console.error('Failed to load watched items:', err);
    } finally {
      setWatchedLoading(false);
    }
  };

  const handleUnwatch = async (item) => {
    setUnwatching(prev => ({ ...prev, [item.subscription_id]: true }));
    try {
      const res = await fetch(`${API_URL}/subscriptions`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ entity_type: item.entity_type, entity_id: item.entity_id })
      });
      if (res.ok) {
        setWatchedItems(prev => prev.filter(w => w.subscription_id !== item.subscription_id));
      }
    } catch (err) {
      console.error('Failed to unwatch:', err);
    } finally {
      setUnwatching(prev => { const n = { ...prev }; delete n[item.subscription_id]; return n; });
    }
  };

  const openDeleteModal = () => {
    setDeleteModal(true);
    setDeleteStep(1);
    setDeleteConfirmText('');
    setDeletePassword('');
    setDeleteError('');
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/auth/account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ password: deletePassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setDeleteError(data.error || 'Failed to delete account.');
        return;
      }
      await logout();
      navigate('/');
      const mainContent = document.querySelector('.main-content');
      if (mainContent) mainContent.scrollTop = 0;
    } catch (err) {
      setDeleteError('An error occurred. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleNotifPrefSave = async () => {
    setNotifPrefSaving(true);
    setNotifPrefError('');
    setNotifPrefSuccess('');
    try {
      const res = await fetch(`${API_URL}/users/me/notification-preferences`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email_digest_frequency: notifPref })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setNotifPref(data.email_digest_frequency);
      setNotifPrefSuccess('Notification preference saved.');
      setTimeout(() => setNotifPrefSuccess(''), 3000);
    } catch (err) {
      setNotifPrefError('Error saving preference: ' + err.message);
    } finally {
      setNotifPrefSaving(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const resp = await fetch(`${API_URL}/billing/create-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await resp.json();
      if (data.url) window.location.href = data.url;
    } catch (err) {
      console.error('Portal error:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  const tierLabel = (tier) => {
    if (tier === 'pro') return 'Pro';
    if (tier === 'business') return 'Business';
    return 'Free';
  };

  const statusLabel = (status) => {
    const labels = {
      active: '✅ Active',
      past_due: '⚠️ Past Due',
      canceled: '❌ Canceled',
      trialing: '🆓 Trial',
      incomplete: '⏳ Incomplete',
      none: '—',
    };
    return labels[status] || status;
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Photo must be less than 5MB');
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleDeletePhoto = async () => {
    if (window.confirm('Are you sure you want to delete your photo?')) {
      try {
        const response = await fetch(`${USERS_API_URL}/users/${user.user_id}/photo`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          setPhotoPreview(null);
          setPhotoFile(null);
          setSuccess('Photo deleted successfully');
          // Refresh user data to update the nav thumbnail
          await refreshUser();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          throw new Error('Failed to delete photo');
        }
      } catch (err) {
        setError('Error deleting photo: ' + err.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Update user profile using the profile-specific endpoint
      const response = await fetch(`${USERS_API_URL}/users/${user.user_id}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          nickname: formData.nickname,
          email: formData.email,
          alternate_email: formData.alternate_email,
          phone: formData.phone
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }

      const responseData = await response.json();
      
      // Check if email change was requested (requires verification)
      if (responseData.emailChangeRequested) {
        setPendingEmail(responseData.pendingEmail);
        setSuccess(responseData.message || 'A verification email has been sent to your new email address.');
        // Reset the email field to current email since change is pending
        setFormData(prev => ({ ...prev, email: responseData.email }));
      }

      // Upload photo if one was selected
      if (photoFile) {
        setUploadingPhoto(true);
        try {
          const photoFormData = new FormData();
          photoFormData.append('photo', photoFile);
          const photoResponse = await fetch(`${USERS_API_URL}/users/${user.user_id}/photo`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: photoFormData
          });

          if (!photoResponse.ok) {
            const photoErr = await photoResponse.json().catch(() => ({}));
            if (photoErr.code === 'IMAGE_MODERATION_BLOCKED') {
              setError(photoErr.error || 'Your photo was blocked for inappropriate content.');
            } else {
              setError('Failed to upload photo.');
            }
          } else {
            // Refresh user data to update the nav thumbnail
            await refreshUser();
          }
        } catch (photoErr) {
          console.error('Photo upload error:', photoErr);
          setError('Failed to upload photo.');
        } finally {
          setUploadingPhoto(false);
        }
      }

      if (!responseData.emailChangeRequested) {
        setSuccess('Profile updated successfully!');
      }
      setPhotoFile(null);
      setTimeout(() => setSuccess(''), 5000);
      
      // Refresh user data
      fetchUserData();
      // Also refresh the auth context user to update nav
      await refreshUser();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="user-profile-screen"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="user-profile-screen">
      <UploadOverlay
        visible={uploadingPhoto}
        message="Uploading photo…"
        subtext="Scanning for content safety"
      />
      <div className="profile-card">
        <h1>My Profile</h1>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit} className={saving ? 'profile-form-saving' : ''}>
          {/* Saving Overlay */}
          {saving && (
            <div className="saving-overlay">
              <div className="saving-spinner" />
              <span>Saving...</span>
            </div>
          )}
          {/* Email Status Warning */}
          {emailStatus && emailStatus.status === 'disabled' && (
            <div className="email-disabled-banner" style={{
              background: '#fff5f5',
              border: '1px solid #fc8181',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px',
              color: '#c53030'
            }}>
              <strong>⚠️ Your email has been disabled</strong>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                We detected a delivery problem with your email address and have disabled it.
                {emailStatus.reason && <><br /><strong>Reason:</strong> {emailStatus.reason}</>}
              </p>
              <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#718096' }}>
                Please update your email address below to restore full functionality. If you believe this is an error, contact support.
              </p>
            </div>
          )}
          {/* Photo Section */}
          <div className="form-group photo-section">
            <label>Profile Photo</label>
            <div className="photo-container">
              {photoPreview ? (
                <img 
                  src={photoPreview}
                  alt="Profile"
                  className="profile-photo"
                  onError={() => setPhotoPreview(null)}
                />
              ) : (
                <div className="photo-placeholder">
                  {formData.username ? formData.username.charAt(0).toUpperCase() : '?'}
                </div>
              )}
              <div className="photo-buttons">
                <button
                  type="button"
                  className="photo-action-button"
                  onClick={() => document.getElementById('photo-upload').click()}
                  disabled={uploadingPhoto || saving}
                >
                  {uploadingPhoto ? 'Uploading...' : 'Choose Photo'}
                </button>
                <input
                  type="file"
                  id="photo-upload"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  style={{ display: 'none' }}
                  disabled={uploadingPhoto || saving}
                />
                {photoPreview && (
                  <button
                    type="button"
                    onClick={handleDeletePhoto}
                    className="photo-action-button delete"
                    disabled={saving}
                  >
                    Delete Photo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={formData.username}
              disabled
              className="disabled-input"
            />
            <small className="field-hint">Username cannot be changed</small>
          </div>

          <div className="form-group">
            <label htmlFor="nickname">Nickname (Display Name)</label>
            <input
              type="text"
              id="nickname"
              value={formData.nickname}
              onChange={(e) => setFormData({...formData, nickname: e.target.value})}
              placeholder="Optional display name"
              disabled={saving}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              disabled={saving}
            />
            {pendingEmail && (
              <div className="pending-email-notice">
                <span className="pending-icon">…</span>
                Pending verification for: <strong>{pendingEmail}</strong>
                <br />
                <small>Check your inbox and click the verification link to complete the email change.</small>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="alternate_email">Alternate Email</label>
            <input
              type="email"
              id="alternate_email"
              value={formData.alternate_email}
              onChange={(e) => setFormData({...formData, alternate_email: e.target.value})}
              placeholder="Backup email address (optional)"
              disabled={saving}
            />
            <small className="field-hint">Used to contact you if your primary email has delivery issues</small>
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="Optional phone number"
              disabled={saving}
            />
            <small className="field-hint">Secondary contact method (optional)</small>
          </div>

          {/* Roles Section - Read Only */}
          <div className="form-group">
            <label>My Roles</label>
            <div className="roles-display">
              {userRoles.length > 0 ? (
                userRoles.map(role => (
                  <div key={role.role_id} className="role-badge">
                    <span className="role-name">{role.role_name}</span>
                    {role.role_description && (
                      <span className="role-description">{role.role_description}</span>
                    )}
                  </div>
                ))
              ) : (
                <div className="no-roles">No roles assigned</div>
              )}
            </div>
          </div>
          
          <div className="form-actions">
            <button type="submit" className="save-button" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button 
              type="button" 
              className="cancel-button"
              onClick={() => navigate(-1)}
              disabled={saving}
            >
              Cancel
            </button>
          </div>

          {/* Security */}
          <div style={{
            marginTop: '1.5rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid #e9ecef'
          }}>
            <label style={{ marginBottom: '0.5rem', display: 'block' }}>Security</label>
            <button
              type="button"
              onClick={() => navigate('/change-password')}
              style={{
                background: 'none',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '0.6rem 1.2rem',
                fontSize: '0.9rem',
                color: '#555',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#6c5ce7';
                e.currentTarget.style.color = '#6c5ce7';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#ddd';
                e.currentTarget.style.color = '#555';
              }}
            >
              🔒 Change Password
            </button>
          </div>

        </form>
      </div>

      {/* ═══ Community Contributions Section ═══ */}
      <div className="profile-card" style={{ marginBottom: '0' }}>
        <h2 className="billing-section-title">Community Contributions
          {user?.is_industry_vip && <IndustryVIPBadge style={{ marginLeft: '10px', verticalAlign: 'middle' }} />}
        </h2>

        {/* Wiki stats */}
        {wikiStats && wikiStats.total_edits > 0 ? (
          <div style={{ marginBottom: discStats && discStats.total_messages > 0 ? '1.25rem' : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.95rem', fontWeight: '600', color: '#555' }}>Wiki Edits</span>
              <WikiBadge trustLevel={wikiStats.trust_level} />
              {wikiStats.trust_level === 0 && (
                <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>No badge yet</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <div>
                <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#6c5ce7' }}>{wikiStats.approved_edits}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Approved</div>
              </div>
              <div>
                <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#333' }}>{wikiStats.total_edits}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Submitted</div>
              </div>
              {wikiStats.rejected_edits > 0 && (
                <div>
                  <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#ef4444' }}>{wikiStats.rejected_edits}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Rejected</div>
                </div>
              )}
            </div>
            <TrustProgressBar trustLevel={wikiStats.trust_level} cleanApprovedEdits={wikiStats.clean_approved_edits || 0} />
          </div>
        ) : (
          <div style={{ color: '#6b7280', fontSize: '0.95rem', marginBottom: discStats && discStats.total_messages > 0 ? '1.25rem' : 0 }}>
            <p style={{ margin: '0 0 0.5rem 0' }}>You haven't contributed any wiki edits yet.</p>
            <p style={{ margin: 0, fontSize: '0.85rem' }}>
              Help improve amp pages by suggesting edits — look for the <strong>"Suggest Edit"</strong> button on any amp's page.
            </p>
          </div>
        )}

        {/* Divider if both sections present */}
        {((wikiStats && wikiStats.total_edits > 0) || true) && discStats && discStats.total_messages > 0 && (
          <div style={{ borderTop: '1px solid #e9ecef', marginBottom: '1.25rem' }} />
        )}

        {/* Discussion stats */}
        {discStats && discStats.total_messages > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.95rem', fontWeight: '600', color: '#555' }}>Discussions</span>
              <DiscussionBadge discussionLevel={discStats.discussion_level} />
              {discStats.discussion_level === 0 && (
                <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>No badge yet</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <div>
                <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#0369a1' }}>{discStats.total_messages}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Posts</div>
              </div>
              <div>
                <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#333' }}>{discStats.total_topics}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Topics Started</div>
              </div>
            </div>
            <DiscussionProgressBar discussionLevel={discStats.discussion_level} totalMessages={discStats.total_messages} />
          </div>
        )}

        <div style={{ marginTop: '0.75rem' }}>
          <a href="/contributors" style={{ color: '#6c5ce7', fontSize: '0.9rem', textDecoration: 'none' }}
             onMouseEnter={e => e.target.style.textDecoration = 'underline'}
             onMouseLeave={e => e.target.style.textDecoration = 'none'}
          >
            View Leaderboard →
          </a>
        </div>
      </div>

      {/* ═══ Subscription & Billing Section ═══ */}
      <div className="profile-card billing-section">
        <h2 className="billing-section-title">Subscription & Billing</h2>

        {billingBanner && (
          <div className={`account-banner account-banner-${billingBanner.type}`}>
            {billingBanner.message}
            <button className="account-banner-close" onClick={() => setBillingBanner(null)}>&times;</button>
          </div>
        )}

        {/* Current Plan */}
        <div className="account-plan-info">
          <div className="account-plan-tier">
            <span className={`account-tier-badge tier-${billing?.tier || 'free'}`}>
              {tierLabel(billing?.tier)}
            </span>
            {billing?.isBillingExempt && (
              <span className="account-exempt-badge">Complimentary</span>
            )}
          </div>

          {billing?.tier !== 'free' && billing?.status && (
            <div className="account-plan-details">
              <div className="account-detail-row">
                <span className="account-detail-label">Status</span>
                <span className="account-detail-value">{statusLabel(billing.status)}</span>
              </div>
              <div className="account-detail-row">
                <span className="account-detail-label">Billing</span>
                <span className="account-detail-value">{billing.billingPeriod === 'annual' ? 'Annual' : 'Monthly'}</span>
              </div>
              {billing.currentPeriodEnd && (
                <div className="account-detail-row">
                  <span className="account-detail-label">
                    {billing.cancelAtPeriodEnd ? 'Access Until' : 'Renews'}
                  </span>
                  <span className="account-detail-value">
                    {new Date(billing.currentPeriodEnd).toLocaleDateString()}
                  </span>
                </div>
              )}
              {billing.cancelAtPeriodEnd && (() => {
                const daysLeft = billing.currentPeriodEnd
                  ? Math.ceil((new Date(billing.currentPeriodEnd) - new Date()) / (1000 * 60 * 60 * 24))
                  : null;
                return (
                  <div className="account-cancel-notice">
                    Your subscription has been cancelled.{' '}
                    {daysLeft > 0
                      ? <>You have <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong> of access remaining.</>
                      : 'Your access has ended.'}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        <div className="account-plan-actions">
          {billing?.tier === 'free' && !billing?.isBillingExempt && (
            <button className="account-btn account-btn-primary" onClick={() => setShowUpgrade(true)}>
              Upgrade to Pro
            </button>
          )}
          {billing?.tier === 'free' && billing?.isBillingExempt && (
            <p className="account-exempt-note">
              Your complimentary access has ended. Contact support or ask an admin to update your account.
            </p>
          )}
          {billing?.hasSubscription && (
            <button 
              className="account-btn account-btn-secondary" 
              onClick={openPortal}
              disabled={portalLoading}
            >
              {portalLoading ? 'Opening...' : 'Manage Subscription'}
            </button>
          )}
          {billing?.tier !== 'free' && !billing?.hasSubscription && !billing?.isBillingExempt && (
            <button className="account-btn account-btn-primary" onClick={() => setShowUpgrade(true)}>
              Resubscribe
            </button>
          )}
        </div>

        {/* Payment History */}
        {billingHistory.length > 0 && (
          <>
            <h3 className="billing-history-title">Payment History</h3>
            <div className="account-history-table">
              <div className="account-history-header">
                <span>Date</span>
                <span>Description</span>
                <span>Amount</span>
                <span>Status</span>
              </div>
              {billingHistory.map(payment => (
                <div key={payment.payment_id} className="account-history-row">
                  <span>{new Date(payment.created_at).toLocaleDateString()}</span>
                  <span>{payment.description}</span>
                  <span>${(payment.amount_cents / 100).toFixed(2)}</span>
                  <span className={`account-payment-status status-${payment.status}`}>
                    {payment.status}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ═══ Notification Preferences Section ═══ */}
      <div className="profile-card billing-section">
        <h2 className="billing-section-title">Notification Preferences</h2>
        <p style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: '-0.75rem', marginBottom: '1.25rem' }}>
          In-app notifications are always on. Choose whether to also receive an email digest for watched items.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
          {[
            { value: 'none',    label: 'No email digest',  desc: 'In-app notifications only. No emails.' },
            { value: 'daily',   label: 'Daily Digest',     desc: 'One summary email each morning at 8:00 UTC.' },
            { value: 'weekly',  label: 'Weekly Digest',    desc: 'One summary email every Monday morning at 8:00 UTC.' },
          ].map(opt => (
            <label
              key={opt.value}
              htmlFor={`notif-pref-${opt.value}`}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}
              data-testid={`notif-option-${opt.value}`}
            >
              <input
                type="radio"
                id={`notif-pref-${opt.value}`}
                name="email_digest_frequency"
                value={opt.value}
                checked={notifPref === opt.value}
                onChange={() => setNotifPref(opt.value)}
                style={{ marginTop: '3px' }}
              />
              <span>
                <span style={{ fontWeight: '500', color: '#2d3748' }}>{opt.label}</span>
                <span style={{ display: 'block', fontSize: '13px', color: '#718096' }}>{opt.desc}</span>
              </span>
            </label>
          ))}
        </div>
        {notifPrefError && <div className="error-message" style={{ marginBottom: '8px' }}>{notifPrefError}</div>}
        {notifPrefSuccess && <div className="success-message" style={{ marginBottom: '8px' }}>{notifPrefSuccess}</div>}
        <button
          type="button"
          className="account-btn account-btn-primary"
          onClick={handleNotifPrefSave}
          disabled={notifPrefSaving}
          data-testid="save-notif-pref"
        >
          {notifPrefSaving ? 'Saving...' : 'Save Notification Preference'}
        </button>
      </div>

      {/* ═══ Watched Items Section ═══ */}
      <div className="profile-card billing-section" data-testid="watched-items-section">
        <h2 className="billing-section-title">Watched Items</h2>
        <p style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: '-0.75rem', marginBottom: '1.25rem' }}>
          You get in-app notifications for all of these. Remove anything you no longer want to follow.
        </p>

        {watchedLoading && (
          <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Loading…</div>
        )}

        {!watchedLoading && watchedItems !== null && watchedItems.length === 0 && (
          <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
            You're not watching anything yet. Items you create or interact with are added automatically.
          </div>
        )}

        {!watchedLoading && watchedItems && watchedItems.length > 0 && (() => {
          // Group by type_label for display
          const groups = {};
          for (const item of watchedItems) {
            const key = item.type_label || item.entity_type;
            if (!groups[key]) groups[key] = [];
            groups[key].push(item);
          }
          return Object.entries(groups).map(([typeLabel, items]) => (
            <div key={typeLabel} style={{ marginBottom: '1.5rem' }}>
              <div style={{
                fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase',
                letterSpacing: '0.06em', color: '#9ca3af', marginBottom: '0.5rem'
              }}>
                {typeLabel} ({items.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {items.map(item => (
                  <div key={item.subscription_id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', background: '#f9fafb', borderRadius: '8px',
                    border: '1px solid #e5e7eb', gap: '12px'
                  }}
                  data-testid={`watched-item-${item.subscription_id}`}
                  >
                    <a
                      href={item.url || '#'}
                      style={{ color: '#2563eb', fontSize: '0.9rem', textDecoration: 'none', flexGrow: 1, minWidth: 0,
                               overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      onMouseEnter={e => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={e => e.target.style.textDecoration = 'none'}
                    >
                      {item.label || `${item.entity_type} #${item.entity_id}`}
                    </a>
                    <button
                      onClick={() => handleUnwatch(item)}
                      disabled={!!unwatching[item.subscription_id]}
                      data-testid={`unwatch-btn-${item.subscription_id}`}
                      style={{
                        flexShrink: 0, background: 'none', border: '1px solid #d1d5db',
                        borderRadius: '6px', padding: '3px 10px', fontSize: '0.8rem',
                        color: '#6b7280', cursor: 'pointer', transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={e => {
                        if (!unwatching[item.subscription_id]) {
                          e.currentTarget.style.borderColor = '#ef4444';
                          e.currentTarget.style.color = '#ef4444';
                        }
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.color = '#6b7280';
                      }}
                    >
                      {unwatching[item.subscription_id] ? '…' : 'Unwatch'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ));
        })()}
      </div>

      {/* ═══ Danger Zone ═══ */}
      <div className="profile-card billing-section">
        <div className="danger-zone">
          <h3 className="danger-zone-title">Danger Zone</h3>
          <div className="danger-zone-action">
            <div>
              <strong>Delete Account</strong>
              <p>Permanently delete your account and all of your data. This cannot be undone.</p>
            </div>
            <button type="button" className="danger-zone-btn" onClick={openDeleteModal}>
              Delete Account
            </button>
          </div>
        </div>
      </div>

      <UpgradeModal
        show={showUpgrade}
        onClose={() => setShowUpgrade(false)}
      />

      {/* ── Delete Account Modal ── */}
      {deleteModal && (
        <div className="delete-modal-overlay" onClick={() => !deleting && setDeleteModal(false)}>
          <div className="delete-modal" onClick={e => e.stopPropagation()}>
            {deleteStep === 1 ? (
              <>
                <h2>Delete your account?</h2>
                <div className="delete-modal-warning">
                  <p style={{ marginTop: 0 }}>This will permanently delete:</p>
                  <ul>
                    <li>Your profile and all account settings</li>
                    <li>All amps in your collection</li>
                    <li>Your showcase and gallery</li>
                    <li>Your active subscription (if any)</li>
                  </ul>
                  <p>Your discussion posts will be anonymized but not removed.</p>
                  <p style={{ marginBottom: 0 }}><strong>This cannot be undone.</strong></p>
                </div>
                <label className="delete-modal-label">
                  Type your username <strong>{formData.username}</strong> to confirm:
                </label>
                <input
                  type="text"
                  className="delete-modal-input"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder={formData.username}
                  autoFocus
                />
                <div className="delete-modal-actions">
                  <button type="button" className="delete-modal-cancel" onClick={() => setDeleteModal(false)}>Cancel</button>
                  <button
                    type="button"
                    className="delete-modal-confirm"
                    disabled={deleteConfirmText !== formData.username}
                    onClick={() => setDeleteStep(2)}
                  >
                    Continue
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2>Enter your password</h2>
                <p className="delete-modal-subtitle">Confirm your password to permanently delete your account.</p>
                {deleteError && <div className="delete-modal-error">{deleteError}</div>}
                <label className="delete-modal-label">Password</label>
                <input
                  type="password"
                  className="delete-modal-input"
                  value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)}
                  placeholder="Your current password"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && !deleting && deletePassword && handleDeleteAccount()}
                />
                <div className="delete-modal-actions">
                  <button type="button" className="delete-modal-cancel" onClick={() => setDeleteStep(1)} disabled={deleting}>Back</button>
                  <button
                    type="button"
                    className="delete-modal-confirm"
                    disabled={!deletePassword || deleting}
                    onClick={handleDeleteAccount}
                  >
                    {deleting ? 'Deleting...' : 'Delete My Account'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileScreen;
